// pages/strategic-playbook/tools/compound-forecaster.js
// ════════════════════════════════════════════════════
// COMPOUND FORECASTER
// ════════════════════════════════════════════════════
// "The most powerful force in the universe is compound
// interest." — Munger
//
// Forces the long-term math: project compounding over
// decades. Side-by-side comparison of trading vs index.
// Cost drag visualization (commissions, taxes, slippage
// destroy compounding).
//
// Connects to: Buffett + Munger
// ════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import Head from 'next/head';
import Link from 'next/link';

export default function CompoundForecaster() {
  const [startingCapital, setStartingCapital] = useState(50000);
  const [monthlyContribution, setMonthlyContribution] = useState(2000);
  const [annualReturnPct, setAnnualReturnPct] = useState(8);
  const [years, setYears] = useState(25);
  const [costDragPct, setCostDragPct] = useState(0);
  const [comparisonReturnPct, setComparisonReturnPct] = useState(8);

  const result = useMemo(() => {
    return forecast({
      startingCapital,
      monthlyContribution,
      annualReturnPct: annualReturnPct / 100,
      years,
      costDragPct: costDragPct / 100,
      comparisonReturnPct: comparisonReturnPct / 100,
    });
  }, [
    startingCapital,
    monthlyContribution,
    annualReturnPct,
    years,
    costDragPct,
    comparisonReturnPct,
  ]);

  return (
    <div className="sp-root">
      <Head>
        <title>Compound Forecaster · Strategic Intelligence</title>
      </Head>

      <div className="sp-page">
        <ToolTopbar />

        <div style={{ marginBottom: 32 }}>
          <Link
            href="/strategic-playbook/tools"
            className="sp-reader-back"
            style={{ marginBottom: 16 }}
          >
            ← Tools
          </Link>
          <div
            style={{
              fontFamily: 'var(--sp-font-mono)',
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.20em',
              color: 'var(--sp-aged-brass)',
              marginBottom: 14,
            }}
          >
            Decision Sizing · Calculator
          </div>
          <h1
            style={{
              fontFamily: 'var(--sp-font-serif)',
              fontWeight: 500,
              fontSize: 32,
              lineHeight: 1.2,
              color: 'var(--sp-warm-cream)',
              margin: '0 0 14px 0',
              letterSpacing: '-0.01em',
            }}
          >
            Compound Forecaster
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.65,
              color: 'rgba(232, 221, 196, 0.78)',
              maxWidth: 700,
              margin: 0,
            }}
          >
            The most powerful force in the universe (Munger). Project compounding over
            decades. Compare your strategy vs index alternative. <strong>Cost drag —
            commissions, taxes, slippage — is the silent killer.</strong>
          </p>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.3fr',
            gap: 24,
          }}
          className="compound-grid"
        >
          {/* INPUTS */}
          <div
            style={{
              padding: '28px',
              background: 'var(--sp-soft-charcoal)',
              border: '1px solid var(--sp-divider)',
              borderRadius: 'var(--sp-radius-card)',
            }}
          >
            <h3
              style={{
                fontFamily: 'var(--sp-font-mono)',
                fontSize: 10,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                color: 'var(--sp-aged-brass)',
                margin: '0 0 22px 0',
              }}
            >
              Inputs
            </h3>

            <Field label="Starting capital" suffix="$">
              <NumberInput value={startingCapital} onChange={setStartingCapital} step={1000} min={0} />
            </Field>

            <Field label="Monthly contribution" suffix="$">
              <NumberInput value={monthlyContribution} onChange={setMonthlyContribution} step={100} min={0} />
            </Field>

            <Field label="Your strategy return" suffix="% / yr" hint="Be honest. After taxes + costs.">
              <NumberInput value={annualReturnPct} onChange={setAnnualReturnPct} step={0.5} min={-50} max={100} />
            </Field>

            <Field
              label="Cost drag"
              suffix="% / yr"
              hint="Commissions + taxes + slippage. Active traders rarely under 2%."
            >
              <NumberInput value={costDragPct} onChange={setCostDragPct} step={0.1} min={0} max={20} />
            </Field>

            <Field label="Years" suffix="yr">
              <NumberInput value={years} onChange={setYears} step={1} min={1} max={60} />
            </Field>

            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--sp-divider)' }}>
              <Field
                label="Comparison: index return"
                suffix="% / yr"
                hint="S&P 500 historical: ~10%. Modest assumption: 8%."
              >
                <NumberInput value={comparisonReturnPct} onChange={setComparisonReturnPct} step={0.5} min={0} max={50} />
              </Field>
            </div>
          </div>

          {/* OUTPUTS */}
          <div>
            {/* Final value comparison */}
            <div
              style={{
                padding: '28px',
                background:
                  'linear-gradient(135deg, var(--sp-card-charcoal) 0%, var(--sp-soft-charcoal) 100%)',
                border: '1px solid var(--sp-aged-brass)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: 'var(--sp-aged-brass)',
                  marginBottom: 14,
                }}
              >
                After {years} years
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                <div>
                  <div
                    style={{
                      fontFamily: 'var(--sp-font-mono)',
                      fontSize: 10,
                      color: 'rgba(232, 221, 196, 0.55)',
                      letterSpacing: '0.10em',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    Your strategy
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--sp-font-serif)',
                      fontSize: 30,
                      lineHeight: 1.1,
                      color: 'var(--sp-warm-cream)',
                      marginBottom: 4,
                    }}
                  >
                    ${result.strategyFinal.toLocaleString()}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--sp-font-mono)',
                      fontSize: 11,
                      color:
                        result.strategyEffectivePct >= 0
                          ? 'var(--sp-forest-moss)'
                          : 'var(--sp-oxblood)',
                    }}
                  >
                    {result.strategyEffectivePct.toFixed(2)}% / yr after costs
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontFamily: 'var(--sp-font-mono)',
                      fontSize: 10,
                      color: 'rgba(232, 221, 196, 0.55)',
                      letterSpacing: '0.10em',
                      textTransform: 'uppercase',
                      marginBottom: 6,
                    }}
                  >
                    Index comparison
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--sp-font-serif)',
                      fontSize: 30,
                      lineHeight: 1.1,
                      color: 'var(--sp-aged-brass)',
                      marginBottom: 4,
                    }}
                  >
                    ${result.comparisonFinal.toLocaleString()}
                  </div>
                  <div
                    style={{
                      fontFamily: 'var(--sp-font-mono)',
                      fontSize: 11,
                      color: 'var(--sp-aged-brass)',
                    }}
                  >
                    {comparisonReturnPct.toFixed(1)}% / yr
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: 22,
                  paddingTop: 16,
                  borderTop: '1px solid var(--sp-divider)',
                  fontFamily: 'var(--sp-font-serif)',
                  fontStyle: 'italic',
                  fontSize: 14,
                  lineHeight: 1.55,
                  color: 'var(--sp-warm-cream)',
                  borderLeft: `2px solid ${result.verdictColor}`,
                  paddingLeft: 14,
                }}
              >
                {result.verdict}
              </div>
            </div>

            {/* Compounding chart - simple ASCII */}
            <div
              style={{
                padding: '24px 28px',
                background: 'var(--sp-soft-charcoal)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: 'var(--sp-aged-brass)',
                  margin: '0 0 18px 0',
                }}
              >
                Year-by-year
              </h3>
              <Chart series={result.chartData} />
            </div>

            {/* Math breakdown */}
            <div
              style={{
                padding: '24px 28px',
                background: 'var(--sp-soft-charcoal)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
                marginBottom: 16,
              }}
            >
              <h3
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  color: 'var(--sp-aged-brass)',
                  margin: '0 0 18px 0',
                }}
              >
                The Math
              </h3>
              <ResultRow label="Total contributed" value={'$' + result.totalContributed.toLocaleString()} />
              <ResultRow label="Strategy net return" value={'$' + (result.strategyFinal - result.totalContributed).toLocaleString()} />
              <ResultRow label="Index net return" value={'$' + (result.comparisonFinal - result.totalContributed).toLocaleString()} />
              <ResultRow
                label="Difference"
                value={
                  (result.strategyFinal - result.comparisonFinal >= 0 ? '+' : '') +
                  '$' +
                  (result.strategyFinal - result.comparisonFinal).toLocaleString()
                }
                color={
                  result.strategyFinal - result.comparisonFinal >= 0
                    ? 'var(--sp-forest-moss)'
                    : 'var(--sp-oxblood)'
                }
                highlight
              />
              <ResultRow
                label="Cost drag total damage"
                value={'-$' + result.costDragDamage.toLocaleString()}
                color="var(--sp-oxblood)"
                hint="What costs would have compounded into"
              />
            </div>

            {/* Honest read */}
            <div
              style={{
                padding: '20px 24px',
                background: 'var(--sp-parchment-bg)',
                border: '1px solid var(--sp-divider)',
                borderRadius: 'var(--sp-radius-card)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  textTransform: 'uppercase',
                  letterSpacing: '0.16em',
                  color: 'var(--sp-aged-brass)',
                  marginBottom: 10,
                }}
              >
                Honest Read
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'rgba(232, 221, 196, 0.78)' }}>
                {result.honestRead}
              </div>
            </div>

            <div style={{ marginTop: 16, fontSize: 12, color: 'rgba(232, 221, 196, 0.55)' }}>
              <span
                style={{
                  fontFamily: 'var(--sp-font-mono)',
                  fontSize: 10,
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'rgba(232, 221, 196, 0.45)',
                  marginRight: 8,
                }}
              >
                Connected dossiers:
              </span>
              <Link href="/strategic-playbook/buffett-compound-certainty" style={{ color: 'var(--sp-aged-brass)' }}>
                Buffett
              </Link>
              {' · '}
              <Link href="/strategic-playbook/munger-lattice-of-models" style={{ color: 'var(--sp-aged-brass)' }}>
                Munger
              </Link>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 900px) {
          .compound-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function forecast({
  startingCapital,
  monthlyContribution,
  annualReturnPct,
  years,
  costDragPct,
  comparisonReturnPct,
}) {
  // Effective return after cost drag
  const effectiveReturn = annualReturnPct - costDragPct;
  const monthlyRate = (1 + effectiveReturn) ** (1 / 12) - 1;
  const monthlyComparisonRate = (1 + comparisonReturnPct) ** (1 / 12) - 1;
  // Reference: no-cost-drag rate to calculate cost damage
  const monthlyNoCostRate = (1 + annualReturnPct) ** (1 / 12) - 1;

  let strategyValue = startingCapital;
  let comparisonValue = startingCapital;
  let noCostValue = startingCapital;
  const chartData = [{ year: 0, strategy: startingCapital, comparison: startingCapital }];

  for (let m = 1; m <= years * 12; m++) {
    strategyValue = strategyValue * (1 + monthlyRate) + monthlyContribution;
    comparisonValue = comparisonValue * (1 + monthlyComparisonRate) + monthlyContribution;
    noCostValue = noCostValue * (1 + monthlyNoCostRate) + monthlyContribution;
    if (m % 12 === 0) {
      chartData.push({
        year: m / 12,
        strategy: Math.round(strategyValue),
        comparison: Math.round(comparisonValue),
      });
    }
  }

  const strategyFinal = Math.round(strategyValue);
  const comparisonFinal = Math.round(comparisonValue);
  const totalContributed = startingCapital + monthlyContribution * years * 12;
  const costDragDamage = Math.round(noCostValue - strategyValue);

  // Verdict
  const diff = strategyFinal - comparisonFinal;
  const diffPct = (diff / comparisonFinal) * 100;
  let verdict, verdictColor, honestRead;

  if (diff >= 0 && annualReturnPct - comparisonReturnPct >= 0.02) {
    verdict = `Your strategy beats index by $${diff.toLocaleString()} over ${years} years (+${diffPct.toFixed(1)}%). ${costDragPct > 1 ? 'But cost drag is destroying value — see below.' : 'Compounding is working for you.'}`;
    verdictColor = 'var(--sp-forest-moss)';
    honestRead =
      costDragPct >= 2
        ? `Your strategy beats index, but cost drag of ${costDragPct}%/yr is silently destroying $${costDragDamage.toLocaleString()} of compounding. If you reduced cost drag to 0.1% (low-cost index level), you'd add $${(noCostValue - comparisonFinal).toLocaleString()} more to the final outcome. Cost drag IS the trade.`
        : `Strategy is beating index AND keeping costs low — that's the rare combination. Buffett's actual edge is largely cost-discipline + tax-efficiency over decades, not stock-picking.`;
  } else if (diff < 0 && diff > -0.10 * comparisonFinal) {
    verdict = `Your strategy underperforms index by $${Math.abs(diff).toLocaleString()} over ${years} years.`;
    verdictColor = 'var(--sp-cognac)';
    honestRead = `You're trailing index by ${Math.abs(diffPct).toFixed(1)}%. ${costDragPct >= 1 ? `Cost drag of ${costDragPct}%/yr is the cause — eliminating it would close the gap. ` : ''}Honest question: is your active strategy creating enough value AFTER costs to justify the time investment? For most active traders, the answer is no. Buffett famously says: index funds for most.`;
  } else {
    verdict = `Strategy underperforms by $${Math.abs(diff).toLocaleString()} (${Math.abs(diffPct).toFixed(1)}%). Significant cost — re-examine.`;
    verdictColor = 'var(--sp-oxblood)';
    honestRead = `Significant gap. Either return assumption is too low, cost drag is too high, or the strategy isn't beating index. Most active traders fall here. Switching to low-cost index would add $${Math.abs(diff).toLocaleString()} over your time horizon.`;
  }

  return {
    strategyFinal,
    comparisonFinal,
    totalContributed,
    strategyEffectivePct: effectiveReturn * 100,
    costDragDamage,
    chartData,
    verdict,
    verdictColor,
    honestRead,
  };
}

function Chart({ series }) {
  if (!series || series.length === 0) return null;
  const max = Math.max(...series.map((d) => Math.max(d.strategy, d.comparison)));

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          height: 160,
          gap: 2,
          paddingBottom: 24,
          borderBottom: '1px solid var(--sp-divider)',
          position: 'relative',
        }}
      >
        {series.map((d) => {
          const stratHeight = (d.strategy / max) * 140;
          const compHeight = (d.comparison / max) * 140;
          return (
            <div
              key={d.year}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                position: 'relative',
                minWidth: 4,
              }}
              title={`Year ${d.year}: Strategy $${d.strategy.toLocaleString()} · Index $${d.comparison.toLocaleString()}`}
            >
              <div
                style={{
                  width: '100%',
                  height: compHeight,
                  background: 'rgba(156, 123, 63, 0.30)',
                  position: 'absolute',
                  bottom: 0,
                  borderRadius: '2px 2px 0 0',
                }}
              />
              <div
                style={{
                  width: '100%',
                  height: stratHeight,
                  background:
                    d.strategy >= d.comparison ? 'var(--sp-forest-moss)' : 'var(--sp-oxblood)',
                  position: 'relative',
                  zIndex: 1,
                  borderRadius: '2px 2px 0 0',
                  opacity: 0.85,
                }}
              />
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: 'var(--sp-font-mono)',
          fontSize: 10,
          color: 'rgba(232, 221, 196, 0.50)',
          marginTop: 6,
        }}
      >
        <span>Year 0</span>
        <span>Year {series[series.length - 1].year}</span>
      </div>
      <div
        style={{
          marginTop: 14,
          display: 'flex',
          gap: 18,
          fontFamily: 'var(--sp-font-mono)',
          fontSize: 10,
          color: 'rgba(232, 221, 196, 0.65)',
          textTransform: 'uppercase',
          letterSpacing: '0.10em',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 8, background: 'var(--sp-forest-moss)' }} />
          Your strategy
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 12, height: 8, background: 'rgba(156, 123, 63, 0.30)' }} />
          Index comparison
        </span>
      </div>
    </div>
  );
}

// Shared UI primitives
function Field({ label, suffix, hint, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          fontFamily: 'var(--sp-font-mono)',
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          color: 'rgba(232, 221, 196, 0.55)',
          marginBottom: 6,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <span>{label}</span>
        {suffix && <span style={{ color: 'var(--sp-aged-brass)' }}>{suffix}</span>}
      </div>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'rgba(232, 221, 196, 0.50)', marginTop: 4, lineHeight: 1.5 }}>{hint}</div>}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--sp-charcoal-black)',
  border: '1px solid var(--sp-divider)',
  borderRadius: 'var(--sp-radius)',
  color: 'var(--sp-warm-cream)',
  fontFamily: 'var(--sp-font-body)',
  fontSize: 14,
  outline: 'none',
};

function NumberInput({ value, onChange, step, min, max }) {
  return (
    <input
      type="number"
      value={value}
      step={step}
      min={min}
      max={max}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      style={inputStyle}
    />
  );
}

function ResultRow({ label, value, color, highlight, hint }) {
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px solid rgba(58, 47, 40, 0.5)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: highlight ? 'var(--sp-warm-cream)' : 'rgba(232, 221, 196, 0.65)' }}>
          {label}
        </span>
        <span
          style={{
            fontFamily: 'var(--sp-font-mono)',
            fontSize: 13,
            color: color || (highlight ? 'var(--sp-aged-brass)' : 'var(--sp-warm-cream)'),
            fontWeight: highlight ? 600 : 400,
          }}
        >
          {value}
        </span>
      </div>
      {hint && <div style={{ fontSize: 10, color: 'rgba(232, 221, 196, 0.45)', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function ToolTopbar() {
  return (
    <div className="sp-topbar">
      <div>
        <h1>Strategic Intelligence</h1>
        <div className="sp-topbar-meta">Tools · Compound Forecaster</div>
      </div>
      <nav className="sp-topbar-nav">
        <Link href="/strategic-playbook">Dashboard</Link>
        <Link href="/strategic-playbook/library">Library</Link>
        <Link href="/strategic-playbook/tools" className="sp-active">Tools</Link>
        <Link href="/strategic-playbook/decisions">Decisions</Link>
        <Link href="/strategic-playbook/simulate">Simulate</Link>
      </nav>
    </div>
  );
}
